/** Branch patterns, git refs and the CI context of a request. */
import { z } from 'zod';
import { BRANCH_PATTERNS_MAX, branchPatternProblem } from './glob.js';

export const BranchPattern = z.string().superRefine((pattern, ctx) => {
  const problem = branchPatternProblem(pattern);
  if (problem) ctx.addIssue({ code: 'custom', message: problem });
});
export const BranchPatterns = z.array(BranchPattern).max(BRANCH_PATTERNS_MAX)
  .refine((patterns) => new Set(patterns).size === patterns.length, 'each pattern may appear only once');
/** One branch, such as `main`: a pattern without `*`. */
export const BranchName = BranchPattern.refine((branch) => !branch.includes('*'), 'a branch name, without *');

/** `GitRef` (api/openapi.yaml): the ref a build or a shadow run is for, as the caller reports it. */
export const GitRef = z.object({
  branch: z.string().min(1).max(255),
  commitSha: z.string().regex(/^[0-9a-f]{40}$/).optional(),
  commitMessage: z.string().max(500).optional(),
});
export type GitRef = z.infer<typeof GitRef>;

export const CiProvider = z.enum(['bitbucket', 'github', 'gitlab', 'other']);

/** `CiContext` (api/openapi.yaml): the CI run that sent the request; informational on a build. */
export const CiContext = z.object({
  provider: CiProvider.optional(),
  workspace: z.string().max(128).optional(),
  repository: z.string().max(128).optional(),
  pipelineUuid: z.string().max(64).optional(),
  buildNumber: z.number().int().min(0).optional(),
  stepUuid: z.string().max(64).optional(),
  url: z.string().max(500).optional(),
  actor: z.string().max(128).optional(),
});
export type CiContext = z.infer<typeof CiContext>;
